import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PiCaretLeft, PiUsers } from 'react-icons/pi'

import {
  ActionsDropdown,
  createEditAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import confirm from 'components/dialogConfirm'
import { Skeleton } from 'components/FbUI/Skeleton'
import { EditSavedFilterModal } from 'components/Filters/saved-filters/EditSavedFilterModal'
import {
  FilterIdentifier,
  SavedView,
  SavedViewTableLabels,
} from 'models/saved_view'
import useApiService from 'services/api'
import { formatDate } from 'utils/formatting'
import { Z_INDEX } from 'styles/z-index'

/**
 * Mobile-only management screen for saved filters — the destination of the
 * "Manage saved filters…" link in the saved-filters dropdown, which on the
 * desktop router resolves to /settings/data/filters (DataSettingsPage) but had
 * no mobile equivalent (FIR-7788 follow-up). Lists every saved view the user
 * can see as a card with rename/share (via EditSavedFilterModal) and delete,
 * reusing the same hooks as the desktop SavedFiltersTable.
 */
export function MobileSavedFiltersPage() {
  const api = useApiService()
  const navigate = useNavigate()

  const { data, isLoading, refetch } = api.useGetAllSavedFilters()

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<SavedView | null>(null)

  const handleEdit = useCallback((filter: SavedView) => {
    setSelectedFilter(filter)
    setEditModalOpen(true)
  }, [])

  const handleDelete = useCallback(
    async (filterId: number) => {
      const confirmed = await confirm(
        'Are you sure you want to delete this saved filter?',
        'Delete Saved Filter',
        'This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )
      if (!confirmed) return

      try {
        await api.deleteFilter(filterId)
        toast.success('Saved filter deleted successfully')
        refetch()
      } catch (err) {
        console.error('Failed to delete saved filter:', err)
        toast.error('Failed to delete saved filter')
      }
    },
    [api, refetch]
  )

  return (
    <div className="flex min-h-full flex-col bg-surface-page">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-surface px-4 py-3">
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="flex items-center justify-center rounded-lg p-1.5 text-fg-tertiary transition-colors hover:bg-surface-sunken hover:text-fg-secondary"
        >
          <PiCaretLeft size={20} />
        </button>
        <h1 className="m-0 text-lg font-semibold text-fg">Saved filters</h1>
      </div>

      {/* Bottom padding clears the fixed MobileFooter bar (60px content +
          safe-area inset) so the last card isn't chopped off. */}
      <div className="flex-1 px-4 pt-4 pb-[calc(60px+env(safe-area-inset-bottom,0px)+1rem)]">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[68px] w-full rounded-xl" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 py-16 text-center">
            <p className="m-0 text-sm font-medium text-fg">
              No saved filters yet
            </p>
            <p className="m-0 text-xs text-fg-secondary">
              Save a filter from any list to manage it here.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {data.map((filter) => {
              const tableLabel =
                SavedViewTableLabels[filter.identifier as FilterIdentifier]
              return (
                <div
                  key={filter.id}
                  className="flex items-start justify-between gap-3 rounded-xl border border-line bg-surface p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-sm font-medium text-fg">
                      {filter.name}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-secondary">
                      {tableLabel && <span>{tableLabel}</span>}
                      {tableLabel && (
                        <span className="text-fg-tertiary">·</span>
                      )}
                      <span>{formatDate(filter.created)}</span>
                      {filter.shared && (
                        <>
                          <span className="text-fg-tertiary">·</span>
                          <span className="inline-flex items-center gap-1">
                            <PiUsers size={12} />
                            Shared
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <ActionsDropdown
                    // Lift above the fixed MobileFooter (z 1000) so the menu on
                    // the last row isn't painted under the bottom bar.
                    menuZIndex={Z_INDEX.MOBILE_DROPDOWN}
                    actions={[
                      createEditAction(() => handleEdit(filter)),
                      createDeleteAction(() => handleDelete(filter.id)),
                    ]}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <EditSavedFilterModal
        show={editModalOpen}
        handleClose={() => {
          setEditModalOpen(false)
          setSelectedFilter(null)
        }}
        savedFilter={selectedFilter}
        onFilterUpdated={refetch}
      />
    </div>
  )
}
